/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useCallback, useMemo, useRef, useState } from "react"
import { notification } from "@duxton-web/ui"

import { useTranslationsMap } from "common/utils/i18n"

import {
  InspectionDecision,
  RETURN_MEDIA_OPERATION,
  RETURN_REVIEW_RESOLUTION,
  ReturnRequestDetail,
  ReturnRequestItem,
  ReviewDecision,
} from "ecommerce/constants/returnRefund"
import { useMinuteTick } from "ecommerce/hooks/useMinuteTick"
import {
  useGetReturnRefundReviewDetailQuery,
  useSubmitReturnedItemInspectionMutation,
  useSubmitReturnRefundReviewDecisionMutation,
  useUploadReturnRefundMediaMutation,
} from "ecommerce/queries"
import {
  applyReviewDecisions,
  getReviewDetailSlaMinutesLeft,
  getReviewSuccessMessage,
  toInspectionPayload,
  toReturnRefundMediaUpload,
  toReturnRequestDetail,
  toReviewDecisionPayload,
} from "ecommerce/utils/returnRefundHelpers"
import { readFileAsDataURL } from "utils/photo_helper"

interface UseReturnRequestDetailParams {
  /** Scopes the request header; the drawer never opens without a store selected. */
  merchantID?: string
  /**
   * The REQUEST is identified by its order: the review-detail endpoint is keyed by
   * `orderID` (MVP allows one submitted request per delivered order). Absent while no
   * request is selected — the query is skipped and `detail` stays undefined.
   */
  orderID?: string
}

interface UseReturnRequestDetailResult {
  /** The requested payload, or undefined while nothing is selected / not yet loaded. */
  detail?: ReturnRequestDetail
  /**
   * Fetching with nothing usable to show — the first load and a retry after a failed
   * one. Deliberately NOT RTK Query's `isLoading`, which stays false across a retry
   * (see `ReturnRefundListContainer` for the full rationale).
   */
  isLoading: boolean
  isError: boolean
  refetch(): void
  /**
   * Submits decision 1 and, on acceptance, applies its outcomes and shows the
   * success toast. Resolves `false` on failure so the caller keeps the form.
   */
  submitDecisions(
    requestID: string,
    decisions: Record<string, ReviewDecision>,
  ): Promise<boolean>
  /**
   * Submits decision 2's outcomes (issue photos are already staged server-side —
   * uploaded on add). Resolves true
   * on success — the CALLER owns the success feedback, because the outcome decides
   * its shape (an all-approve gets the illustrated sheet, a reported issue does
   * not); failures are already toasted by the base query.
   */
  submitInspections(
    requestID: string,
    decisions: Record<string, InspectionDecision>,
  ): Promise<boolean>
  /**
   * Submits the once-per-item REQUEST_INFO decision for the given items — one
   * batch, nothing else: the clarify hand-off is a real decision, submitted
   * alone so the merchant's other unsaved picks stay local. The server opens
   * (or reuses) the request's buyer chatroom in the same save and attaches
   * every submitted item to it; resolves with the response's roomID ("" when
   * it carried none) or undefined on failure, which the base query has
   * already toasted.
   */
  requestClarification(
    requestID: string,
    items: ReturnRequestItem[],
  ): Promise<string | undefined>
  /**
   * Stages one photo as a server-side draft the moment the merchant adds it.
   * Resolves with the staged assetID (the invalidated detail refetch then echoes
   * it back in the item's `draftPhotos`, and the tile dedupes on the id); rejects
   * on failure so the caller can drop the tile and toast. Uploads run one at a
   * time in add order.
   */
  uploadPhoto(
    item: ReturnRequestItem,
    uploadPurpose: string,
    file: File,
    /**
     * Which picker produced the file. Omitted for a browser file input, whose
     * source is always the device library; the webview's Add-photos sheet passes
     * the row the merchant chose, so a camera capture is reported as one.
     */
    mediaSource?: string,
  ): Promise<string>
  /**
   * Deletes one server-staged draft photo by asset id. Resolves `true` when the
   * server dropped it — the invalidated detail refetch then stops echoing it in
   * `draftPhotos`; `false` on failure (already toasted), so the caller can put
   * the tile back.
   */
  removePhoto(
    item: ReturnRequestItem,
    uploadPurpose: string,
    assetID: string,
  ): Promise<boolean>
  /**
   * True while any photo call is still in flight — an upload staging a draft or
   * a removal deleting one. Confirm waits on this: submitting mid-removal lets
   * the server consume a draft the merchant already took away.
   */
  isMediaBusy: boolean
}

/**
 * A request's drawer payload plus the decision submission, so the desktop list and the
 * webview page share one behaviour rather than wiring the mutation twice — the drift two
 * independent hosts otherwise guarantee.
 *
 * Both halves are real: the review-detail query and the review-decision mutation. The
 * decision deltas stay as the OPTIMISTIC BRIDGE — stored on a successful submit and
 * re-applied over the cached base, so the drawer reflects the outcome instantly while
 * the tag invalidation refetches; once the fresh base arrives with the server's own
 * progression, `applyReviewDecisions`' awaiting-decision guard makes the re-apply a
 * no-op and the server's truth stands.
 */
export const useReturnRequestDetail = ({
  merchantID,
  orderID,
}: UseReturnRequestDetailParams): UseReturnRequestDetailResult => {
  const text = useTranslationsMap({
    refundSuccess: "ecommerce.sellerCentre.returns.refundSuccess",
    reviewSuccess: "ecommerce.sellerCentre.returns.reviewSuccess",
  })

  /*
   * Local decision DELTAS, keyed by requestID so switching between requests cannot
   * bleed one request's optimistic state onto another. Deltas rather than a frozen
   * result payload: they re-apply onto every fresh base the query delivers, so a
   * refetch that shows real server-side progression is never shadowed by a stale
   * snapshot. They bridge the window between an accepted submit and the invalidated
   * refetch landing — and stay across later refetches, where the awaiting-decision
   * guard turns the re-apply into a no-op once the server has caught up.
   */
  const [decisionsByRequest, setDecisionsByRequest] = useState<
    Record<string, Record<string, ReviewDecision>>
  >({})

  const {
    currentData,
    isFetching,
    isError,
    refetch,
  } = useGetReturnRefundReviewDetailQuery(
    { merchantID: merchantID ?? "", orderID: orderID ?? "" },
    { skip: !merchantID || !orderID },
  )

  /*
   * `currentData`, never `data`: `data` retains the LAST fulfilled result across an
   * argument change, so opening request B would render request A's detail (no
   * skeleton, wrong items, wrong CTA) until B's response lands — and keep rendering
   * it if B's fetch fails. `currentData` resets on the argument change, which is
   * exactly the boundary a request switch is.
   */
  const base = useMemo(() => toReturnRequestDetail(currentData), [currentData])

  const [submitReviewDecision] = useSubmitReturnRefundReviewDecisionMutation()
  const [
    submitReturnedItemInspection,
  ] = useSubmitReturnedItemInspectionMutation()
  const [uploadReturnRefundMedia] = useUploadReturnRefundMediaMutation()

  /*
   * The upload queue's tail — always a settled-safe promise, so one failed upload
   * never wedges the queue for the photos added after it.
   */
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve())

  /*
   * Counted rather than boolean: uploads and removals share one queue, and the
   * flag must stay true until the LAST of them settles.
   */
  const [pendingMediaCount, setPendingMediaCount] = useState(0)
  const trackMedia = useCallback(<T>(work: Promise<T>): Promise<T> => {
    setPendingMediaCount(count => count + 1)
    work.then(
      () => setPendingMediaCount(count => count - 1),
      () => setPendingMediaCount(count => count - 1),
    )

    return work
  }, [])

  /*
   * Uploads on ADD, not at Confirm: each photo stages a server-side draft the
   * moment it is picked, the tag invalidation refetches the detail, and the
   * item's `draftPhotos` becomes the durable record the form renders. The
   * eventual REJECT/report-issue decision consumes whatever is staged.
   *
   * SEQUENTIAL deliberately: add order is draft order server-side, and one flight
   * at a time keeps the per-item photo-count validation from racing itself when
   * the merchant multi-selects.
   */
  const uploadPhoto = useCallback(
    (
      item: ReturnRequestItem,
      uploadPurpose: string,
      file: File,
      mediaSource?: string,
    ): Promise<string> => {
      if (!merchantID || !orderID) {
        return Promise.reject(new Error("missing merchant or order scope"))
      }

      const upload = async (): Promise<string> => {
        const dataUrl = await readFileAsDataURL(file)

        const response = await uploadReturnRefundMedia({
          merchantID,
          orderID,
          itemID: item.itemID ?? item.id,
          displayModifierGroup: item.displayModifierGroup ?? "",
          uploadPurpose,
          // Explicit rather than inferred — an ADD must carry no replacement id.
          operation: RETURN_MEDIA_OPERATION.add,
          media: toReturnRefundMediaUpload(file, dataUrl, mediaSource),
          // The card names the file in its own toast — see the endpoint.
          suppressErrorToast: true,
        }).unwrap()

        const assetID = response.media?.assetID
        if (!assetID) {
          /*
           * A staged photo with no identity cannot be deduped against its own
           * echoed draft (nor removed), so the tile would double on the next
           * refetch. Treat it as a failed upload instead.
           */
          throw new Error("upload response carried no assetID")
        }

        return assetID
      }

      const settled = trackMedia(uploadQueueRef.current.then(upload))
      // The caller sees this photo's own outcome; the queue swallows it so the
      // next photo still runs.
      uploadQueueRef.current = settled.then(
        () => undefined,
        () => undefined,
      )

      return settled
    },
    [merchantID, orderID, trackMedia, uploadReturnRefundMedia],
  )

  /*
   * Same endpoint, delete semantics: `REMOVE` carries the asset id ALONE — the
   * server rejects a remove that also carries `media`. Queued behind the uploads
   * so a removal cannot overtake a photo still being staged for the same item.
   */
  const removePhoto = useCallback(
    (
      item: ReturnRequestItem,
      uploadPurpose: string,
      assetID: string,
    ): Promise<boolean> => {
      if (!merchantID || !orderID || !assetID) {
        return Promise.resolve(false)
      }

      const remove = (): Promise<boolean> =>
        uploadReturnRefundMedia({
          merchantID,
          orderID,
          itemID: item.itemID ?? item.id,
          displayModifierGroup: item.displayModifierGroup ?? "",
          uploadPurpose,
          operation: RETURN_MEDIA_OPERATION.remove,
          mediaAssetIDToReplace: assetID,
        })
          .unwrap()
          .then(() => true)
          // The base query already showed the error notification.
          .catch(() => false)

      const settled = trackMedia(uploadQueueRef.current.then(remove))
      uploadQueueRef.current = settled.then(
        () => undefined,
        () => undefined,
      )

      return settled
    },
    [merchantID, orderID, trackMedia, uploadReturnRefundMedia],
  )

  // One flight at a time across the whole Confirm flow — see the lock below.
  const isSubmittingRef = useRef(false)

  /*
   * Resolves `true` only when the server accepted the decisions — the caller keeps
   * the review form intact on `false` so a failed submission (the base query's
   * default error notification reports it) never costs the merchant their
   * selections. Rejection photos are already staged server-side (uploaded on add);
   * the decision submit consumes them. The mutation blocks the UI globally for the
   * round trip; on success the deltas land and the toast shows.
   */
  const submitDecisions = useCallback(
    async (
      id: string,
      decisions: Record<string, ReviewDecision>,
    ): Promise<boolean> => {
      // A miss cannot happen from the drawer (it only opens on resolved details),
      // but the guard keeps a stray caller from mutating the wrong request.
      if (!base || base.requestID !== id || !merchantID || !orderID) {
        return false
      }

      // Re-entry lock: a rapid second Confirm racing the first POST must not
      // submit the same decisions twice.
      if (isSubmittingRef.current) {
        return false
      }
      isSubmittingRef.current = true

      try {
        await submitReviewDecision({
          merchantID,
          orderID,
          requestID: id,
          decisions: toReviewDecisionPayload(base.items, decisions),
        }).unwrap()
      } catch {
        // The base query already shows the error notification; this only keeps
        // the rejection from surfacing as unhandled.
        return false
      } finally {
        isSubmittingRef.current = false
      }

      setDecisionsByRequest(previous => ({
        ...previous,
        // Later passes merge over earlier ones — per item, the newest wins.
        [id]: { ...previous[id], ...decisions },
      }))

      // notification.* is the house toast in src/ecommerce (message.* has no
      // other call site in the tree).
      notification.success({
        message: getReviewSuccessMessage(decisions, {
          refundSuccess: text.refundSuccess,
          reviewSuccess: text.reviewSuccess,
        }),
      })

      return true
    },
    // `text.refundSuccess`/`text.reviewSuccess`, not `text` itself:
    // `useTranslationsMap` returns a new `text` object every render, which
    // would rebuild this callback every render for no reason — only these
    // two strings actually feed `getReviewSuccessMessage`.
    [
      base,
      merchantID,
      orderID,
      submitReviewDecision,
      text.refundSuccess,
      text.reviewSuccess,
    ],
  )

  /*
   * No optimistic delta and no toast: the item's move to Info requested (and its
   * spent `infoRequestUsed` flag) arrives with the invalidated refetch, and the
   * caller owns the destination — the chat screen IS the success feedback.
   */
  const requestClarification = useCallback(
    async (
      id: string,
      items: ReturnRequestItem[],
    ): Promise<string | undefined> => {
      if (
        !base ||
        base.requestID !== id ||
        !merchantID ||
        !orderID ||
        items.length === 0
      ) {
        return undefined
      }

      // Shares the Confirm lock: a clarify racing a decision submit (or another
      // clarify) must not land a second batch.
      if (isSubmittingRef.current) {
        return undefined
      }
      isSubmittingRef.current = true

      try {
        const response = await submitReviewDecision({
          merchantID,
          orderID,
          requestID: id,
          decisions: items.map(item => ({
            itemID: item.itemID ?? item.id,
            decision: RETURN_REVIEW_RESOLUTION.requestInfo,
            displayModifierGroup: item.displayModifierGroup ?? "",
          })),
        }).unwrap()

        // "" when the server carried no room (the swagger gap) — the caller sends
        // it to the app as-is, which owns what an unaddressed chat opens.
        return response.roomID ?? ""
      } catch {
        // The base query already shows the error notification (including the
        // once-per-item INFO_REQUEST_ALREADY_USED conflict).
        return undefined
      } finally {
        isSubmittingRef.current = false
      }
    },
    [base, merchantID, orderID, submitReviewDecision],
  )

  /*
   * Decision 2. No optimistic deltas, unlike the review: an approved refund and a
   * reported issue both land on server-owned states (refund processing, GS review)
   * that the FE should not guess at — the invalidated refetch, arriving right behind
   * the blocking submit, is the source of truth.
   */
  const submitInspections = useCallback(
    (
      id: string,
      decisions: Record<string, InspectionDecision>,
    ): Promise<boolean> => {
      if (!base || base.requestID !== id || !merchantID || !orderID) {
        return Promise.resolve(false)
      }

      /*
       * Same re-entry lock as `submitDecisions` — a rapid second Confirm would
       * fire a second POST (rejected server-side, but surfacing an error toast
       * over the success sheet). Shared ref, so an in-flight review submit blocks
       * inspection too.
       */
      if (isSubmittingRef.current) {
        return Promise.resolve(false)
      }
      isSubmittingRef.current = true

      return (
        submitReturnedItemInspection({
          merchantID,
          orderID,
          requestID: id,
          inspections: toInspectionPayload(base.items, decisions),
        })
          .unwrap()
          .then(() => true)
          // The base query already showed the error notification; the caller only
          // needs to know not to celebrate.
          .catch(() => false)
          .finally(() => {
            isSubmittingRef.current = false
          })
      )
    },
    [base, merchantID, orderID, submitReturnedItemInspection],
  )

  const decisions = base ? decisionsByRequest[base.requestID] : undefined
  const detail = useMemo(
    () => (base && decisions ? applyReviewDecisions(base, decisions) : base),
    [base, decisions],
  )

  /*
   * The mapped `slaMinutesLeft` is a snapshot of `Date.now()` frozen by the memo
   * above, so it is re-derived on a minute tick — otherwise the footer countdown
   * would overstate the time remaining for as long as the drawer stays open. Only an
   * actionable detail ticks (a closed request has no countdown to drive), and only a
   * still-present deadline is re-derived: `applyReviewDecisions` clears the countdown
   * when nothing is left to act on, and that clearance must stick.
   */
  const dueAt = currentData?.timeline?.action?.dueAt
  useMinuteTick(detail?.slaMinutesLeft !== undefined)

  return {
    detail:
      detail && detail.slaMinutesLeft !== undefined
        ? { ...detail, slaMinutesLeft: getReviewDetailSlaMinutesLeft(dueAt) }
        : detail,
    /*
     * `!base` rather than `!currentData`: a malformed success payload (no requestID)
     * maps to no detail, and the caller's not-found state should show once fetching
     * settles rather than a skeleton that never resolves.
     */
    isLoading: isFetching && !base,
    isError,
    refetch,
    submitDecisions,
    submitInspections,
    requestClarification,
    uploadPhoto,
    removePhoto,
    isMediaBusy: pendingMediaCount > 0,
  }
}
